import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

function AdminEnrollments() {
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProgram, setFilterProgram] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    fetchEnrollments();
    fetchPrograms();
  }, []);

  useEffect(() => {
    fetchEnrollments();
  }, [filterProgram, filterStatus]);

  const fetchEnrollments = async () => {
    try {
      let url = '/admin/enrollments?';
      if (filterProgram) url += `programId=${filterProgram}&`;
      if (filterStatus) url += `status=${filterStatus}&`;
      if (searchTerm) url += `search=${searchTerm}`;

      const res = await api.get(url);
      setEnrollments(res.data);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchPrograms = async () => {
    try {
      const res = await api.get('/admin/programs');
      setPrograms(res.data);
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  };

  const viewDetails = async (enrollmentId) => {
    try {
      const res = await api.get(`/admin/enrollments/${enrollmentId}`);
      setSelectedEnrollment(res.data);
      setShowDetailModal(true);
    } catch (error) {
      alert('Error loading enrollment details');
    }
  };

  const updateStatus = async (enrollmentId, newStatus) => {
    try {
      await api.patch(`/admin/enrollments/${enrollmentId}/status`, { status: newStatus });
      fetchEnrollments();
    } catch (error) {
      alert(error.response?.data?.message || 'Error updating status');
    }
  };

  const handleSearch = () => {
    fetchEnrollments();
  };

  return (
    <div className="container-fluid mt-4">
      <div className="row mb-3">
        <div className="col">
          <h2>Enrollment Management</h2>
        </div>
      </div>

      {/* Filters */}
      <div className="row mb-3">
        <div className="col-md-3">
          <select 
            className="form-select"
            value={filterProgram}
            onChange={(e) => setFilterProgram(e.target.value)}
          >
            <option value="">All Programs</option>
            {programs.map(p => (
              <option key={p._id} value={p._id}>{p.title}</option>
            ))}
          </select>
        </div>
        <div className="col-md-2">
          <select 
            className="form-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="screening-pending">Screening Pending</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
          </select>
        </div>
        <div className="col-md-5">
          <div className="input-group">
            <input 
              type="text" 
              className="form-control"
              placeholder="Search by student name, email, or program..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button className="btn btn-primary" onClick={handleSearch}>
              Search
            </button>
          </div>
        </div>
        <div className="col-md-2">
          <button className="btn btn-secondary w-100" onClick={() => {
            setFilterProgram('');
            setFilterStatus('');
            setSearchTerm('');
            fetchEnrollments();
          }}>
            Clear Filters
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center"><div className="spinner-border"></div></div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th>Student</th>
                <th>Email</th>
                <th>Program</th>
                <th>Category</th>
                <th>Status</th>
                <th>Progress</th>
                <th>Enrolled</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {enrollments.map((enrollment) => (
                <tr key={enrollment._id}>
                  <td><strong>{enrollment.user?.name}</strong></td>
                  <td>{enrollment.user?.email}</td>
                  <td>{enrollment.program?.title}</td>
                  <td>
                    <span className="badge bg-secondary">
                      {enrollment.user?.category} - {enrollment.user?.skillTier}
                    </span>
                  </td>
                  <td>
                    <span className={`badge bg-${
                      enrollment.enrollmentStatus === 'completed' ? 'success' :
                      enrollment.enrollmentStatus === 'active' ? 'primary' :
                      'warning'
                    }`}>
                      {enrollment.enrollmentStatus}
                    </span>
                  </td>
                  <td>
                    <div className="progress" style={{height: '20px'}}>
                      <div 
                        className="progress-bar" 
                        role="progressbar" 
                        style={{width: `${enrollment.overallProgress || 0}%`}}
                      >
                        {enrollment.overallProgress || 0}%
                      </div>
                    </div>
                  </td>
                  <td>{new Date(enrollment.createdAt).toLocaleDateString()}</td>
                  <td>
                    <button 
                      className="btn btn-sm btn-info me-1"
                      onClick={() => viewDetails(enrollment._id)}
                    >
                      Details
                    </button>
                    <div className="btn-group btn-group-sm">
                      <button 
                        className="btn btn-sm btn-secondary dropdown-toggle"
                        data-bs-toggle="dropdown"
                      >
                        Status
                      </button>
                      <ul className="dropdown-menu">
                        <li>
                          <button 
                            className="dropdown-item"
                            onClick={() => updateStatus(enrollment._id, 'active')}
                          >
                            Active
                          </button>
                        </li>
                        <li>
                          <button 
                            className="dropdown-item"
                            onClick={() => updateStatus(enrollment._id, 'completed')}
                          >
                            Completed
                          </button>
                        </li>
                      </ul>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedEnrollment && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Enrollment Details</h5>
                <button className="btn-close" onClick={() => setShowDetailModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <h6>Student Information</h6>
                    <p><strong>Name:</strong> {selectedEnrollment.enrollment?.user?.name}</p>
                    <p><strong>Email:</strong> {selectedEnrollment.enrollment?.user?.email}</p>
                    <p><strong>Category:</strong> {selectedEnrollment.enrollment?.user?.category}</p>
                    <p><strong>Skill Tier:</strong> {selectedEnrollment.enrollment?.user?.skillTier}</p>
                  </div>
                  <div className="col-md-6">
                    <h6>Enrollment Information</h6>
                    <p><strong>Program:</strong> {selectedEnrollment.enrollment?.program?.title}</p>
                    <p><strong>Status:</strong> {selectedEnrollment.enrollment?.enrollmentStatus}</p>
                    <p><strong>Overall Progress:</strong> {selectedEnrollment.enrollment?.overallProgress}%</p>
                    <p><strong>Screening:</strong> {selectedEnrollment.enrollment?.screeningCompleted ? 'Completed' : 'Pending'}</p>
                  </div>
                </div>

                <h6>Section Progress</h6>
                <div className="table-responsive mb-3">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Section</th>
                        <th>Status</th>
                        <th>Assessment Score</th>
                        <th>Completed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedEnrollment.enrollment?.sectionsProgress?.map((section) => (
                        <tr key={section.sectionOrder}>
                          <td>{section.sectionName}</td>
                          <td>
                            <span className={`badge bg-${
                              section.status === 'completed' ? 'success' :
                              section.status === 'in-progress' ? 'primary' :
                              'secondary'
                            }`}>
                              {section.status}
                            </span>
                          </td>
                          <td>{section.assessmentScore || '-'}</td>
                          <td>{section.completedAt ? new Date(section.completedAt).toLocaleDateString() : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h6>Assessment Attempts</h6>
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Assessment</th>
                        <th>Section</th>
                        <th>Attempt #</th>
                        <th>Score</th>
                        <th>Percentage</th>
                        <th>Passed</th>
                        <th>Submitted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedEnrollment.submissions?.map((sub) => (
                        <tr key={sub._id}>
                          <td>{sub.assessment?.title}</td>
                          <td>{sub.assessment?.sectionName}</td>
                          <td>{sub.attemptNumber}</td>
                          <td>{sub.score}/{sub.totalPoints}</td>
                          <td>{sub.percentage}%</td>
                          <td>
                            <span className={`badge bg-${sub.passed ? 'success' : 'danger'}`}>
                              {sub.passed ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td>{new Date(sub.submittedAt).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminEnrollments;
